/**
 * @file StatementPreviewModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-10-20
 *
 * A statement's documents previewed in the app: the shared Modal with the PDF
 * drawn as page images (PdfCanvasPreview) — no browser tab and no PDF viewer
 * toolbar to save or print from (Cam, 19 Oct 2026: no statement preview may
 * open a new tab). The bytes come from POST /api/statements/:id/preview-pdf,
 * which honours `include` (both / statement / invoices) and `template`
 * (email / print) exactly as the old GET /:id/preview did, and logs the
 * preview (PREVIEWED) in the statement's history.
 */
import React from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import PdfCanvasPreview from './PdfCanvasPreview';
import './StatementPreviewModal.css';

const INCLUDE_WORDS = { both: 'Statement and invoices', statement: 'Statement only', invoices: 'Invoices only' };
const TEMPLATE_WORDS = { email: 'for email / screens', print: 'for print' };

/** The request that fetches a statement preview's bytes. */
// `content`: the run's Contents choices (listUsedCredits, attachUsedCredits,
// includeAllUnpaidInvoices, suppressOverdueStamp) so a preview shows what goes.
export async function fetchStatementPreview(statementId, { include = 'both', template = 'email', ...content } = {}) {
  const r = await fetch(`${API_URL}/api/statements/${statementId}/preview-pdf`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ include, template, ...content })
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error || 'Could not build the preview');
  }
  return r.arrayBuffer();
}

function StatementPreviewModal({ statementId, include = 'both', template = 'email', title, onClose }) {
  if (!statementId) return null;
  const subtitle = `${INCLUDE_WORDS[include] || INCLUDE_WORDS.both}, ${TEMPLATE_WORDS[template] || TEMPLATE_WORDS.email}`;
  return (
    <Modal isOpen={true} onClose={onClose} size="xl" className="statement-preview" title={title || 'Statement preview'}>
      <p className="spm-subtitle">{subtitle}</p>
      <PdfCanvasPreview
        className="spm-pages"
        loadKey={`statement:${statementId}:${include}:${template}`}
        load={() => fetchStatementPreview(statementId, { include, template })}
      />
    </Modal>
  );
}

export default StatementPreviewModal;
